import { useCallback, useSyncExternalStore } from 'react';

/**
 * Subscribe to a CSS media query from JS (#1545).
 *
 * CSS handles presentation, but some responsive decisions are structural —
 * the mobile transaction filters render as a bottom sheet rather than an
 * inline bar, and the CSV export button is not rendered at all on handsets.
 * Those cannot be a `display: none`, so the breakpoint has to reach JS.
 *
 * useSyncExternalStore rather than useState + useEffect: matchMedia IS an
 * external store, and the effect form would both flash the wrong branch on
 * first paint and trip the repo's strict `react-hooks/set-state-in-effect`
 * rule (see CLAUDE.md).
 *
 * SSR/no-matchMedia environments (jsdom without a stub) report false, which
 * for every current caller means "desktop" — the richer surface.
 */
export function useMediaQuery(query) {
  const subscribe = useCallback((onChange) => {
    const mql = window.matchMedia?.(query);
    if (!mql) return () => {};
    // Safari < 14 only has the deprecated addListener/removeListener pair.
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  const getSnapshot = useCallback(
    () => window.matchMedia?.(query)?.matches ?? false,
    [query],
  );

  // Server snapshot: no viewport to measure, so report the desktop branch.
  const getServerSnapshot = useCallback(() => false, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * The app's handset breakpoint. Matches the 600px the ledger-sheet CSS
 * already uses for its bottom-sheet presentation (see LedgerSheet.jsx and
 * the `ledger-sheet` rules), so JS and CSS agree on what "mobile" means.
 */
export const MOBILE_QUERY = '(max-width: 600px)';

/**
 * The full breakpoint set (#1857). CSS uses exactly these widths — see
 * docs/design/ui-tokens.md and scripts/check-ui-tokens.mjs, which rejects any
 * other width in an @media query:
 *   compact  ≤400px  small phones only
 *   phone    ≤600px  handsets; the tab bar shows (above: 601px+)
 *   tablet   ≤900px  tablets / narrow windows (above: 901px+)
 *   wide     ≤1200px roomy desktop layouts   (above: 1201px+)
 */
export const TABLET_QUERY = '(max-width: 900px)';

export function useIsMobile() {
  return useMediaQuery(MOBILE_QUERY);
}

/** The OS "reduce motion" setting (#1882): the chart kit honours it in CSS
 *  + useMorphPath, so charts neither draw in nor morph under it. */
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

export function useReducedMotion() {
  return useMediaQuery(REDUCED_MOTION_QUERY);
}
